import { useEffect, useRef, useState } from 'react';
/** Material UI Packages. */
import { Box, LinearProgress, Paper, Tooltip, Typography } from '@mui/material';
import { randomId } from '@mui/x-data-grid-generator';

/** Material UI Icons. */
import {
    UploadFile as UploadFileIcon,
    Close as CloseIcon,
    CloudDone as CloudDoneIcon,
    Help as HelpIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
} from '@mui/icons-material';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Filesize.js Packages. */
import { filesize } from 'filesize';

/** Internal Components. */
import { callAPI } from 'shared/api/callApi';
import DragAndDropFileType from './DragAndDropFileType';
import { fileTypeMap } from 'shared/config/constantsData';

/** Component for rendering file upload progress. */
const ProgressStatus = ({ progress }) => {
    /**
     *  The progress object contains a status key, which determines the type of component to render.
     *  `-1` indicates that the file failed to upload.
     *  `0` indicated that the file is uploading.
     *  `1` indicates that the file has been uploaded successfully.
     * */
    switch (progress.status) {
        case -1:
            return (
                <>
                    <ErrorIcon color="error" />
                    &nbsp;
                    <Typography color="error" fontSize={14} fontWeight="bold">
                        Failed to Upload
                    </Typography>
                </>
            );
        case 0:
            return (
                <>
                    <LinearProgress variant="determinate" value={progress.rate} sx={{ width: '83%', mr: 1 }} />
                    <Typography>{progress.rate} %</Typography>
                </>
            );
        case 1:
            return (
                <>
                    <CheckCircleIcon color="primary" />
                    &nbsp;
                    <Typography color="primary" fontSize={14} fontWeight="bold">
                        Upload Complete
                    </Typography>
                </>
            );
    }
};

/**
 * @param {string} from it's the parent component of DragAndDrop, it MUST be the same module that used in callAPI().
 * @param {array of object} existFileList file information that includes file name and size.
 * @param {boolean} validate validation that used when file is required.
 * @param {function} setCalled function of parent's state modifier.
 * @param {function} setFileUploadLoading function of setting loading on dialog's save button.
 * @param {array of string} fileExtensions array of accept extension, currently element should be lower case.
 * @param {number} maxFileSize max size of total upload files.
 * @param {string} phrase customized guide sentence.
 */
export default function DragAndDrop(props) {
    const {
        moduleName,
        existFileList,
        validate,
        setCalled,
        setFileUploadLoading,
        fileExtensions = ['*'],
        maxFileSize,
        phrase = null,
        sx,
        componentName = null,
    } = props;

    /** Destructuring values from useAuth0 hook. */
    const { user, getAccessTokenSilently } = useAuth0();

    /** State for event of dragging. */
    const [isDragging, setIsDragging] = useState(false);
    /** State for saving upload file. */
    const [newFile, setNewFile] = useState([]);
    /** State for saving exist file. */
    const [existingFile, setExistingFile] = useState([]);
    /** State for file empty status. */
    const [empty, setEmpty] = useState(true);
    /** State for rendering the upload progress. */
    const [progress, setProgress] = useState([]);
    /** Reference of file upload input. */
    const fileInputRef = useRef();
    /** Variable of filtered fileExtensions prop. */
    const acceptFileExtensions = fileExtensions.includes('*') ? Object.keys(fileTypeMap) : fileExtensions;

    /** Detect when the files are empty. */
    useEffect(() => {
        if (newFile.length === 0 && existingFile.length === 0) setEmpty(true);
    }, [newFile, existingFile]);

    /** Refresh existingFile when different row of datagrid has selected.  */
    useEffect(() => {
        if (existFileList != null && existFileList.length > 0) {
            setExistingFile(existFileList);
            setEmpty(false);
        }
    }, [existFileList]);

    /** Handler for file upload input. */
    const handleInputClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    /** Handler for validating the file format. */
    const isFileAcceptable = (files) => {
        for (const file of files) {
            const extension = file.name.split('.').pop();

            // Validate that the uploaded file's type is acceptable and ensure the mime type matches the file extension.
            if (!acceptFileExtensions.includes(extension) && fileTypeMap[extension] !== file.type) {
                alert('The file format is not supported.');
                return false;
            }
            if (file.size >= maxFileSize) {
                alert('The file is too big.');
                return false;
            }
        }
        return true;
    };

    /** Function for generating random id for a newly uploaded file. */
    const generateIdToFiles = (files) => {
        return files.map((file) => {
            const id = randomId();
            setProgress((prev) => [...prev, { id: id, rate: 0, status: 0 }]);
            return { info: file, id: id };
        });
    };

    /** Function for API configuration to calculate file upload progress. */
    const createUploadConfig = (id, name) => {
        return {
            onUploadProgress: (progressEvent) => {
                const { loaded, total } = progressEvent;
                setProgress((progresses) =>
                    progresses.map((progress) =>
                        progress.id === id ? { ...progress, rate: Math.floor((loaded / total) * 100) } : progress,
                    ),
                );
            },
        };
    };

    /** Handler for file drop and upload.
     *  The file object must be received separately depending on the event type.
     *  After the validation, a new id is generated for each file, and the file is then uploaded to temporary S3 bucket.
     */
    const handleFileDrop = async (event) => {
        event.preventDefault();

        let files;
        setIsDragging(false);
        // Assign new upload files that comes from different file location based on each event.type.
        if (event.type === 'change') {
            files = [...fileInputRef.current?.files];
        } else if (event.type === 'drop') {
            files = [...event.dataTransfer?.files];
        }
        if (files.length <= 0 || !isFileAcceptable(files)) return;
        //  Generate new id to upload file.
        files = generateIdToFiles(files);
        setNewFile((prev) => [...prev, ...files]);

        try {
            setFileUploadLoading(true);
            setEmpty(false);
            // When multiple files are selected for upload, multiple POST API will be made for each file.
            // On successful upload, update the progress state to "success" and save the file id to the state.
            // On failed upload, update the progress state to "fail" without saving the file id.
            const uploadFileToTempS3 = await Promise.all(
                files.map(async (file) => {
                    const formData = new FormData();
                    formData.append('il_file', file.info, file.info.name);
                    formData.append('id', JSON.stringify(file.id));
                    const uploadConfig = createUploadConfig(file.id);
                    await callAPI({
                        method: 'post',
                        module: moduleName,
                        component: componentName,
                        type: 'blob',
                        getAccessTokenSilently,
                        email: user.email,
                        obj: formData,
                        onUploadProgress: uploadConfig.onUploadProgress,
                        content_type: 'multipart/form-data',
                        onSuccess: (resp) => {
                            console.log('file upload success', resp);
                            setProgress((prev) =>
                                prev.map((item) => (item.id === file.id ? { ...item, status: 1 } : item)),
                            );
                            if (moduleName === 'duty_calculator' && componentName === 'duty_entry_detail') {
                                // Each invoice file is parsed by its own request, so append the rows
                                // instead of replacing; replacing kept only the last file's items when
                                // several invoices were uploaded at once. source_file_id ties each row
                                // back to its file so deleting a file can remove just its rows.
                                const parsed = JSON.parse(resp.data);
                                const parsedRows = Array.isArray(parsed) ? parsed : [];
                                setCalled((prev) => [
                                    ...(Array.isArray(prev) ? prev : []),
                                    ...parsedRows.map((row) => ({ ...row, source_file_id: file.id })),
                                ]);
                            } else setCalled((prev) => ({ ...prev, il_file: [file.id, ...prev.il_file] }));
                        },
                        onError: () => {
                            setProgress((prev) =>
                                prev.map((item) => (item.id === file.id ? { ...item, status: -1 } : item)),
                            );
                        },
                    });
                }),
            );
        } catch (error) {
            console.error('Error !', error);
        } finally {
            setFileUploadLoading(false);
        }
    };

    /** Handler for delete file.
     *  Newly uploaded files and existing files are managed separeately in different states.
     *  After a file is uploaded to S3 bucket, its existence is determined solely by its file id.
     *  Therefore, to delete a file, simply filter its id from the relevant state.
     */
    const deleteFile = (id, name) => {
        switch (name) {
            case 'newFile':
                setNewFile((uploaded) => uploaded.filter((file) => file.id !== id));
                break;
            case 'existingFile':
                setExistingFile((exist) => exist.filter((file) => file.id !== id));
                break;
        }
        if (moduleName === 'duty_calculator' && componentName === 'duty_entry_detail') {
            // Parsed rows carry the backend's own row ids, so match on the uploading
            // file's id (source_file_id) to drop only that file's preview rows.
            setCalled((prev) => (Array.isArray(prev) ? prev.filter((item) => item.source_file_id !== id) : []));
        } else {
            setCalled((prev) => ({
                ...prev,
                il_file: prev.il_file.filter((file) => file !== id),
            }));
        }
    };

    /** Component for rendering upload guide. */
    const uploadRule = (() => {
        // When phrase prop is null, render default rule phrase.
        if (phrase === null) {
            const capitalizedExtensions = acceptFileExtensions.map((ext) => ext.toUpperCase());
            const lastExtension = capitalizedExtensions.pop();
            // Calculate the file size and format it with the appropriate unit (e.g., KB, MB) using the jedec standard.
            const fileSize = filesize(maxFileSize, { standard: 'jedec' });
            // When the accept file extension is less than 6, directly render the types.
            return acceptFileExtensions.length <= 5 ? (
                <Typography sx={{ fontSize: '12px' }}>
                    {capitalizedExtensions.join(', ')} and {lastExtension} formats, up to {fileSize}, each under 10MB
                </Typography>
            ) : (
                // When the accept file extension is more than 5, render tooltip icon.
                <Box sx={{ display: 'flex', alignContent: 'bottom' }}>
                    <Tooltip title={`Accept file types: ${capitalizedExtensions.join(', ')}, ${lastExtension}`}>
                        <HelpIcon color="info" />
                    </Tooltip>
                    <Typography>Accept files up to {fileSize}, each under 10MB</Typography>
                </Box>
            );
        } else {
            return `${phrase}`;
        }
    })();

    return (
        <Box
            sx={{
                mb: 2,
                py: empty ? 2 : 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: empty ? 'center' : '',
                borderStyle: 'dashed',
                borderRadius: '10px',
                backgroundColor: isDragging ? 'rgba(0, 0, 0, 0.5)' : '#fafafa',
                border: 'dashed 2px',
                borderColor: validate ? 'red' : 'black',
                overflow: 'hidden',
                ...sx,
            }}
            onDrop={handleFileDrop}
            onDragEnter={(e) => {
                e.preventDefault();
                setIsDragging(true);
            }}
            onDragLeave={(e) => {
                e.preventDefault();
                setIsDragging(false);
            }}
            onDragOver={(e) => e.preventDefault()}
            maxHeight="52vh"
        >
            {empty ? (
                <>
                    <UploadFileIcon sx={{ fontSize: 70 }} />
                    <Box display="flex">
                        <Typography onClick={handleInputClick} sx={{ cursor: 'pointer' }}>
                            <b>
                                <u>Click to upload</u>
                            </b>
                            &nbsp;
                        </Typography>
                        <Typography>or drag and drop</Typography>
                    </Box>
                    {uploadRule}
                    <input
                        type="file"
                        accept={acceptFileExtensions.map((ext) => `.${ext}`).join(' ')}
                        ref={fileInputRef}
                        multiple
                        hidden
                        onChange={handleFileDrop}
                    />
                </>
            ) : (
                <Box overflow="scroll">
                    {existingFile.map((file) => {
                        const file_size = filesize(file.il_file_size, { standard: 'jedec' });
                        const file_name = file.il_file_name;
                        const name = 'existingFile';
                        return (
                            <Box key={file.id} sx={{ m: 2, p: 2, border: 'solid 2px', borderRadius: '10px' }}>
                                <Box sx={{ display: 'flex', gap: '10px' }}>
                                    <Paper sx={{ display: 'flex', height: '32px', mt: '5px' }}>
                                        <CloudDoneIcon sx={{ fontSize: '32px' }} />
                                    </Paper>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography>{file_name}</Typography>
                                            <CloseIcon
                                                onClick={() => deleteFile(file.id, name)}
                                                sx={{ cursor: 'pointer' }}
                                            />
                                        </Box>
                                        <Typography>{file_size}</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography>Exist</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}
                    {newFile.map((file) => {
                        const fileExtension = file.info.name.split('.').pop();
                        const file_size = filesize(file.info.size, { standard: 'jedec' });
                        const selectedProgress = progress.find((item) => item.id === file.id);
                        const name = 'newFile';
                        return (
                            <Box key={file.id} sx={{ m: 2, p: 2, border: 'solid 2px', borderRadius: '10px' }}>
                                <Box sx={{ display: 'flex', gap: '10px' }}>
                                    <Paper sx={{ display: 'flex', height: '32px', mt: '5px' }}>
                                        <DragAndDropFileType fileType={fileExtension} />
                                    </Paper>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography>{file.info.name}</Typography>
                                            <CloseIcon
                                                onClick={() => deleteFile(file.id, name)}
                                                sx={{ cursor: 'pointer' }}
                                            />
                                        </Box>
                                        <Typography>{file_size}</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <ProgressStatus progress={selectedProgress} />
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
}
